<template>
  <div class="page-wrap home-page">
    <div class="home-page__stack">
      <div class="home-page__header">
        <DashboardHeader :user="user" :notifications="notifications" />
      </div>

      <div v-if="confirmSuccess" class="home-alert home-alert--success">
        {{ confirmSuccess }}
      </div>

      <div v-if="confirmError" class="home-alert home-alert--danger">
        {{ confirmError }}
      </div>

      <section class="home-hero">
        <div class="home-hero__content">
          <div class="home-hero__head">
            <div class="scanner-panel">
              <template v-if="pendingQrPayload">
                <div class="scanner-panel__confirm">
                  <div class="scanner-panel__confirm-icon">
                    <BaseIcon name="shield" />
                  </div>

                  <div class="scanner-panel__confirm-title">
                    Подтвердить вход
                  </div>

                  <div class="scanner-panel__confirm-text">
                    {{ pendingQrPayload.pcLabel || 'ПК' }}
                  </div>

                  <button
                    class="scanner-panel__action"
                    type="button"
                    :disabled="confirmLoading"
                    @click="confirmQrLogin"
                  >
                    {{ confirmLoading ? 'Подтверждение…' : 'Войти на ПК' }}
                  </button>

                  <button
                    class="scanner-panel__ghost"
                    type="button"
                    @click="resetScanner"
                  >
                    Сканировать заново
                  </button>
                </div>
              </template>

              <template v-else-if="scannerRequested">
                <div class="scanner-panel__live">
                  <div v-if="scannerLoading" class="scanner-panel__loading">
                    Открываю камеру…
                  </div>

                  <div id="home-inline-qr-reader" class="scanner-panel__reader"></div>

                  <button
                    class="scanner-panel__ghost scanner-panel__ghost--live"
                    type="button"
                    @click="stopScanner"
                  >
                    Закрыть сканер
                  </button>
                </div>
              </template>

              <template v-else>
                <div class="scanner-panel__placeholder">
                  <div class="scanner-panel__placeholder-icon">
                    <BaseIcon name="qr" />
                  </div>

                  <div class="scanner-panel__placeholder-title">
                    Вход по QR
                  </div>

                  <div class="scanner-panel__placeholder-text">
                    Отсканируйте QR-код, чтобы войти в аккаунт на компьютере или ресепшене.
                  </div>

                  <button class="scanner-panel__action" type="button" @click="startScanner">
                    Сканировать
                  </button>

                  <div v-if="scannerError" class="scanner-panel__error">
                    {{ scannerError }}
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode';
import DashboardHeader from '../components/home/DashboardHeader.vue';
import BaseIcon from '../components/ui/BaseIcon.vue';
import { getUpcomingBookings, sortBookings } from '../utils/bookingState';
import { post, isSuccess, extractMessage } from '../services/api';

export default {
  name: 'HomeView',
  components: {
    DashboardHeader,
    BaseIcon,
  },
  data() {
    return {
      scannerLoading: false,
      scannerActive: false,
      scannerRequested: false,
      scannerBusy: false,
      scannerError: '',
      confirmError: '',
      confirmSuccess: '',
      confirmLoading: false,
      scannerInstance: null,
      pendingQrPayload: null,
    };
  },
  computed: {
    user() {
      return this.$store.getters['auth/user'];
    },
    items() {
      return this.$store.getters['orders/items'];
    },
    currentClub() {
      return this.$store.getters['clubs/currentClub'];
    },
    bookingTimeZone() {
      return this.currentClub?.timezone || this.currentClub?.club_timezone;
    },
    bookingGroups() {
      return sortBookings(this.items, this.bookingTimeZone);
    },
    notifications() {
      return getUpcomingBookings(this.items, this.bookingTimeZone).length;
    },
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch('clubs/fetchClubs'),
      this.$store.dispatch('orders/loadOrders', { reset: true }),
    ]);
  },
  beforeUnmount() {
    this.stopScanner();
  },
  methods: {
    parseQrPayload(rawValue) {
      const raw = String(rawValue || '').trim();
      if (!raw) return null;

      try {
        const parsed = JSON.parse(raw);
        if (parsed?.type === 'computerclub-login' && parsed?.token) {
          return {
            publicToken: String(parsed.token),
            pcLabel: parsed.pc_label || 'ПК',
            pcUid: parsed.pc_uid || null,
          };
        }
      } catch (error) {}

      try {
        const url = new URL(raw);
        const token = url.searchParams.get('token');
        if (token) {
          return {
            publicToken: String(token),
            pcLabel: url.searchParams.get('pc_label') || 'ПК',
            pcUid: url.searchParams.get('pc_uid') || null,
          };
        }

        const pathMatch = url.pathname.match(/\/login\/([A-Za-z0-9_-]+)/);
        if (pathMatch?.[1]) {
          return {
            publicToken: String(pathMatch[1]),
            pcLabel: url.searchParams.get('pc_label') || 'ПК',
            pcUid: url.searchParams.get('pc_uid') || null,
          };
        }
      } catch (error) {}

      if (/^[A-Za-z0-9_-]{8,}$/.test(raw)) {
        return {
          publicToken: raw,
          pcLabel: 'ПК',
          pcUid: null,
        };
      }

      return null;
    },

    onScannerSuccess(decodedText) {
      if (this.scannerBusy) return;

      const payload = this.parseQrPayload(decodedText);
      if (!payload) {
        this.scannerError = 'QR-код не распознан. Попробуй другой код.';
        return;
      }

      this.scannerBusy = true;
      this.pendingQrPayload = payload;
      this.scannerError = '';
      this.stopScanner();
    },

    onScannerError() {},

    async startScanner() {
      this.confirmError = '';
      this.confirmSuccess = '';
      this.scannerError = '';
      this.pendingQrPayload = null;
      this.scannerRequested = true;
      this.scannerLoading = true;
      this.scannerBusy = false;

      await this.$nextTick();

      try {
        if (!this.scannerInstance) {
          this.scannerInstance = new Html5Qrcode('home-inline-qr-reader');
        }

        await this.scannerInstance.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 220, height: 220 },
            aspectRatio: 1,
          },
          this.onScannerSuccess,
          this.onScannerError,
        );

        this.scannerActive = true;
      } catch (error) {
        this.scannerRequested = false;
        this.scannerError = 'Не удалось открыть камеру. Разреши доступ и попробуй снова.';
      } finally {
        this.scannerLoading = false;
      }
    },

    async stopScanner() {
      this.scannerLoading = false;

      if (this.scannerInstance && this.scannerActive) {
        try {
          await this.scannerInstance.stop();
        } catch (error) {}
      }

      if (this.scannerInstance) {
        try {
          await this.scannerInstance.clear();
        } catch (error) {}
      }

      this.scannerActive = false;
      this.scannerRequested = false;
      this.scannerBusy = false;
    },

    async resetScanner() {
      this.pendingQrPayload = null;
      this.confirmError = '';
      this.confirmSuccess = '';
      await this.startScanner();
    },

    async confirmQrLogin() {
      if (!this.pendingQrPayload?.publicToken) return;

      this.confirmLoading = true;
      this.confirmError = '';
      this.confirmSuccess = '';

      try {
        const response = await post('/pc-login/confirm', {
          hash: this.user?.hash,
          public_token: this.pendingQrPayload.publicToken,
        });

        if (!isSuccess(response)) {
          throw new Error(extractMessage(response, 'Не удалось подтвердить вход'));
        }

        this.confirmSuccess =
          response?.message ||
          `Вход подтверждён: ${this.pendingQrPayload.pcLabel || 'ПК'}`;

        this.pendingQrPayload = null;
        await this.$store.dispatch('orders/loadOrders', { reset: true });
      } catch (error) {
        this.confirmError = error?.message || 'Не удалось подтвердить вход';
      } finally {
        this.confirmLoading = false;
      }
    },
  },
};
</script>

<style scoped src="./HomeView.css"></style>